import React, {useCallback, useEffect, useRef, useState} from "react";
import {useRecoilState, useRecoilValue} from "recoil";
import {selectedColonicalBookId, selectedColonicalBookInfo} from "../../../recoils/recoil";
import {IlamListData} from "./IlamListData";
import ColonicalSchoolToc from "./ColonicalSchoolToc";
import ColonicalSchoolItem from "./ColonicalSchoolItem";
import ColonicalInfoModal from "./ColonicalInfoModal";
import ReactModal from "react-modal";

/**
 * 아카이브 > 조선제학교 일람 목록
 * @returns {JSX.Element}
 * @constructor
 * 목록과 목차가 같이 있음. 조건에 따라 목록을 보여줄지 목차를 보여줄지 나누어짐
 */
const ColonicalSchool = () => {

    const limit = 10;
    const [datas, setDatas] = useState([]);
    const [filteredDatas, setFilteredDatas] = useState([]);
    const [page, setPage] = useState(1);
    const [totalCnt, setTotalCnt] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(true);
    const observerRef = useRef(null);
    const index = useRef(0);
    const [isTocOpen, setIsTocOpen] = useState(false);
    const [selectedBookId, setSelectedBookId] = useRecoilState(selectedColonicalBookId);
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
    const [year, setYear] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const bookInfo = useRecoilValue(selectedColonicalBookInfo);

    // 데이터 로드 로직
    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const res = await fetchAllIlamListData();
                setDatas(res);
                setTotalCnt(res.length);
                const initialData = res.slice(0, limit);
                setFilteredDatas(initialData);
                index.current = 10;
            } catch (error) {
                console.error(error.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData(isTocOpen, selectedBookId );
        // 책 목차에서 목록으로 올 때 랜더링 다시 하기 위해 설정.
        // 원래는 빈 배열임
    }, [isTocOpen, selectedBookId]);

    // 스크롤 시 데이터 가져오기
    const getMoreData = useCallback(async () => {

        // 다음 페이지가 없으면 데이터 불러오기 x
        if (!hasNextPage) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        try {
            const startIndex = index.current;
            const endIndex = index.current + limit;
            const moreData = datas.slice(startIndex, endIndex);

            setFilteredDatas((prevData) => [...prevData, ...moreData]);
            index.current = endIndex;
            setPage((prevPage) => prevPage + 1);

            if (totalCnt <= index.current && totalCnt !== 0) {
                setHasNextPage(false);
            }
        } catch (error) {
            console.error(error.message);
        } finally {
            setIsLoading(false);
        }
    }, [hasNextPage, datas, limit, totalCnt]);

    // 관찰자 등록 및 조건 충족 시 데이터 가져오기
    useEffect(() => {
        const onIntersection = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting === true && hasNextPage === true && isLoading === false) {
                    getMoreData();
                }
            });
        };

        const observer = new IntersectionObserver(onIntersection, {
            threshold: 1.0, // 요소가 완전히 보일 때만 콜백 실행
        });

        const currentObserverRef = observerRef.current;

        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef); // 지역 변수 사용
            }
            observer.disconnect();
        };
    }, [getMoreData, hasNextPage, isLoading, page]);
    
    // 기본 데이터 불러오기
    const fetchAllIlamListData = async () => {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(IlamListData);
            }, 500);
        });
    };

    const handleToc = (bookId) => {
        setIsTocOpen(true);
        setIsInfoModalOpen(false);
        setSelectedBookId(bookId);
    };

    const handleInfo = (year) => {
        setIsTocOpen(false);
        setIsInfoModalOpen(true);
        setYear(year);
    };

    const handleBackInfoToList = () => {
        setIsTocOpen(false);
        setIsInfoModalOpen(false);
        setSelectedBookId(null);
    };

    // 목차 -> 목록으로 이동하는 함수
    const handleBackTocToList = () => {
        setIsTocOpen(false);
        setIsInfoModalOpen(false);
        setSelectedBookId(null);

        // 스크롤 관련 상태 초기화
        setPage(1);
        index.current = 0;
        setHasNextPage(true);
    };

    // 조건에 따른 렌더링 분리
    let content;

    if (!isTocOpen && !selectedBookId) {
        // 로딩 창
        content = (
            <div className="list-btm">
                <div className="table-wrap">
                    <div className="scroll-table">
                        <ColonicalSchoolItem
                            data={filteredDatas}
                            onTocClick={handleToc}
                            onInfoClick={handleInfo}
                        />
                        <div className="load-wrap type-full" style={{display: isLoading ? 'block' : 'none'}}>
                            <div className="load-box">
                                <div className="square"></div>
                                <div className="square"></div>
                                <div className="square"></div>
                                <div className="square"></div>
                            </div>
                        </div>
                        <div ref={observerRef}></div>
                    </div>
                </div>
            </div>
        );
    } else if (selectedBookId !== null || (isTocOpen && !isInfoModalOpen)) {
        // 각 일람에 대한 목차
        content = <ColonicalSchoolToc backToList={handleBackTocToList} />;
    }

    return (
        <>
            <div id="container">
                <main id="contents">
                    <div className="sub-content">
                        <div className="archive-wrap">
                            <div className="cont-top">
                                <div className="w-set">
                                    <div className="title-wrap">
                                        <h2 className="title batang">조선제학교일람</h2>
                                        <p className="text">조선제학교일람은 개별 학교 정보를 확인할 수 있습니다.</p>
                                    </div>

                                </div>
                            </div>
                            <div className="cont-btm">
                                {
                                    selectedBookId !== null || (isTocOpen && !isInfoModalOpen) ?
                                        (

                                            <div className="archive-text-box">
                                                <div className="w-set">
                                                    <div className="text-wrap">
                                                        <p className="list-name">
                                                            <span>{bookInfo ? bookInfo.numeration : "데이터 로드 중..."}</span>
                                                            <span>{bookInfo ? bookInfo.book_title : "데이터 로드 중..."}</span>
                                                            <span>{bookInfo ? bookInfo.book_title_ko : "데이터 로드 중..."}</span>
                                                            <span>{bookInfo ? bookInfo.publish_date : "데이터 로드 중..."}</span>
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            )
                                        : (
                                    <div className="archive-text-box"
                                    style={{display: selectedBookId !== null || (isTocOpen && !isInfoModalOpen) ? 'none' : ''}}>
                                <div className="w-set">

                                    <div className="text-wrap">
                                        <p>
                                            <strong>
                                                센터 조사 결과, 『일람』은 1920년을 제외한 1917년부터 1943년까지 총 26권이 국내외
                                                기관에 보관 중입니다.
                                            </strong>
                                        </p>
                                        <p className="text">
                                            이를 바탕으로 센 터는 43,345건의 교육 통계를 입력했으며, 연도별 표로도 제공하고 있습니다. 표는
                                            7~20개의 항목으로
                                            나뉘며,
                                            학교 수, 학급 수, 교원 수, 학생 수, 학교 운영 비용 등의 정보가 포함되어 있습니다. 개별 학교 정보를
                                            보면 학교명, 위치,
                                            설립
                                            연도도 자세히 기록되어 있습니다.
                                        </p>
                                        <p className="text">
                                            『일람』 에서 확인할 수 있는 특이사항은 다음과 같습니다. 첫째, 1921년, 1923년, 1924년,
                                            1925년 자료에서는
                                            소학교와
                                            보통학교에
                                            대한 구체적인 정보보다 전체 통계가 중심이었습니다. 둘째, 1936년부터 1943년까지는 보통학교, 소학교,
                                            초등학교 현황이 군
                                            단위로
                                            간략하게
                                            기록되었습니다. 마지막으로, 원본 자료에는 학교명, 지역, 통계 숫자 등에서 일부 오류가 있지만, 입력할
                                            당시 발견된 오류를
                                            그대로 남겨
                                            두고
                                            있습니다.
                                        </p>
                                    </div>

                                    <div className="img-wrap">
                                        <div className="archive-slides">
                                            <ul className="slides-wrapper">
                                                <li>
                                                    <div className="imgbox"><img
                                                        src="/images/colonicalSchool/colonicalSchool_bookCover.jpg"
                                                        alt="조선제학교일람 표지"/></div>
                                                    <p className="caption">조선제학교일람 표지</p>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>

                                        )
                                }

                                <div className="list-wrap">
                                    <div className="w-set">
                                        <div className="list-top"
                                             style={{display: selectedBookId !== null || (isTocOpen && !isInfoModalOpen) ? 'none' : 'block'}}>
                                            <strong>조선제학교일람 목록</strong>
                                        </div>
                                        {/*분리된 랜더링*/}
                                        {content}

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </main>

                {/*년도 별 조선제학교 일람 정보*/}
                {isInfoModalOpen && (
                    <ReactModal
                        isOpen={isInfoModalOpen !== null}
                        onRequestClose={handleBackInfoToList}
                        contentLabel="년도 별 조선제학교 일람 정보"
                        style={{
                            overlay: {
                                backgroundColor: "rgba(0, 0, 0, 0.5)",
                                zIndex: 1000,
                            },
                            content: {
                                inset: "unset",
                                padding: 0,
                                border: "none",
                                borderRadius: 0,
                                background: "transparent",
                                width: "auto",
                                height: "auto",
                            },
                        }}
                    >

                        <div className={`wbz-popup-cont is-template size-s ${isInfoModalOpen ? 'on' : ''}`}
                             id="colonialSchoolInfoPopup">
                            <ColonicalInfoModal year={year} backToList={handleBackInfoToList}/>
                        </div>
                    </ReactModal>
                )}
            </div>
        </>
    );
};

export default ColonicalSchool;
